package top.wcpe.allincore.dsl.ir.expr

import kotlin.jvm.JvmStatic
import kotlin.math.floor

/**
 * 把 [ExprNode] 反向格式化为 DSL 源码风格的字符串，便于错误诊断。
 *
 * 与 [ExprNode.toString] 的 AST dump 形式（`MemberCall(MemberAccess(...))`）
 * 不同，本工具产出与用户写的 DSL 源码尽可能一致的输出（`slot.read("x").displayName.contains("y")`），
 * 让用户从错误日志直接看到自己写过的表达式。
 *
 * 受 IR wire 不携带 source position 限制，本工具产出的字符串无法 100% 还原原始空格 / 换行 / 注释；
 * 仅在表达式语义层面等价。Lambda / when / if 等控制流形态以最简表达式形式呈现，省略 body 详情。
 *
 * 与编译期 ExpressionParser 的 token 形态对齐：
 * - 字面量按类型还原（字符串带引号 + 转义、数字保留 toString、布尔 true/false、null）
 * - BinaryOp 用中缀（`a == b`、`a || b`）
 * - MemberAccess / MemberCall 用点 + 圆括号（`a.b`、`a.b(c)`）
 * - SafeMemberAccess / SafeMemberCall 用 `?.`（`a?.b`）
 * - Ternary 用 `cond ? a : b`
 * - 嵌套表达式按运算优先级加括号；为了简洁，对低优先级操作（如 ||）一律加括号
 */
object ExprFormatter {

    /**
     * 表达式优先级（数值越大优先级越高）。仅用于决定是否需要加括号。
     */
    private object Precedence {
        const val LOWEST = 0
        const val TERNARY = 1 // a ? b : c
        const val ELVIS = 2 // a ?: b
        const val LOGICAL_OR = 3 // ||
        const val LOGICAL_AND = 4 // &&
        const val EQUALITY = 5 // == != is
        const val RELATIONAL = 6 // < <= > >=
        const val RANGE = 7 // .. until
        const val ADDITIVE = 8 // + -
        const val MULTIPLICATIVE = 9 // * / %
        const val UNARY = 10 // - ! prefix
        const val POSTFIX = 11 // a.b a?.b a(b) a[b]
        const val ATOM = 12 // 字面量 / 标识符 / 括号包裹
    }

    /** 返回 DSL 源码风格的字符串；node 为 null 时返回 "<null>"。 */
    @JvmStatic
    fun format(node: ExprNode?): String {
        if (node == null) return "<null>"
        val sb = StringBuilder()
        appendExpr(sb, node, /*parentPrec*/ Precedence.LOWEST)
        return sb.toString()
    }

    private fun appendExpr(sb: StringBuilder, node: ExprNode, parentPrec: Int) {
        when (node) {
            is ExprNode.Literal -> appendLiteral(sb, node.value)
            is ExprNode.NullLit -> sb.append("null")
            is ExprNode.StateRef -> sb.append("state.").append(node.name)
            is ExprNode.SessionRef -> sb.append("session.").append(node.name)
            is ExprNode.PersistRef -> sb.append("persist.").append(node.name)
            is ExprNode.IdentRef -> sb.append(node.name)
            is ExprNode.LocalRef -> sb.append(node.name)
            is ExprNode.ThemeRef -> sb.append("theme.").append(node.key)
            is ExprNode.MemberAccess -> withPrec(sb, parentPrec, Precedence.POSTFIX) {
                appendExpr(sb, node.target, Precedence.POSTFIX)
                sb.append('.').append(node.member)
            }
            is ExprNode.SafeMemberAccess -> withPrec(sb, parentPrec, Precedence.POSTFIX) {
                appendExpr(sb, node.target, Precedence.POSTFIX)
                sb.append("?.").append(node.member)
            }
            is ExprNode.MemberCall -> withPrec(sb, parentPrec, Precedence.POSTFIX) {
                appendExpr(sb, node.target, Precedence.POSTFIX)
                sb.append('.').append(node.method)
                appendArgs(sb, node.args)
            }
            is ExprNode.SafeMemberCall -> withPrec(sb, parentPrec, Precedence.POSTFIX) {
                appendExpr(sb, node.target, Precedence.POSTFIX)
                sb.append("?.").append(node.method)
                appendArgs(sb, node.args)
            }
            is ExprNode.BinaryOp -> appendBinary(sb, node, parentPrec)
            is ExprNode.UnaryOp -> withPrec(sb, parentPrec, Precedence.UNARY) {
                sb.append(node.op)
                appendExpr(sb, node.operand, Precedence.UNARY)
            }
            is ExprNode.Ternary -> withPrec(sb, parentPrec, Precedence.TERNARY) {
                appendExpr(sb, node.cond, Precedence.TERNARY + 1)
                sb.append(" ? ")
                appendExpr(sb, node.thenExpr, Precedence.TERNARY + 1)
                sb.append(" : ")
                appendExpr(sb, node.elseExpr, Precedence.TERNARY)
            }
            is ExprNode.Elvis -> withPrec(sb, parentPrec, Precedence.ELVIS) {
                appendExpr(sb, node.left, Precedence.ELVIS + 1)
                sb.append(" ?: ")
                appendExpr(sb, node.right, Precedence.ELVIS)
            }
            is ExprNode.IsExpr -> withPrec(sb, parentPrec, Precedence.EQUALITY) {
                appendExpr(sb, node.target, Precedence.EQUALITY + 1)
                sb.append(" is ").append(node.typeName)
            }
            is ExprNode.Index -> withPrec(sb, parentPrec, Precedence.POSTFIX) {
                appendExpr(sb, node.target, Precedence.POSTFIX)
                sb.append('[')
                appendExpr(sb, node.index, Precedence.LOWEST)
                sb.append(']')
            }
            is ExprNode.Range -> withPrec(sb, parentPrec, Precedence.RANGE) {
                appendExpr(sb, node.start, Precedence.RANGE + 1)
                sb.append(if (node.kind == ExprNode.RangeKind.CLOSED) ".." else " until ")
                appendExpr(sb, node.endValue, Precedence.RANGE + 1)
            }
            is ExprNode.ListLiteral -> {
                sb.append('[')
                val elems = node.elements
                for (i in elems.indices) {
                    if (i > 0) sb.append(", ")
                    appendExpr(sb, elems[i], Precedence.LOWEST)
                }
                sb.append(']')
            }
            is ExprNode.MapLiteral -> {
                sb.append("#{")
                val entries = node.entries
                for (i in entries.indices) {
                    if (i > 0) sb.append(", ")
                    val e = entries[i]
                    sb.append('"').append(e.key).append("\": ")
                    appendExpr(sb, e.value, Precedence.LOWEST)
                }
                sb.append('}')
            }
            is ExprNode.Interp -> {
                // 字符串插值：还原为带 ${} 的字符串字面量
                sb.append('"')
                for (part in node.parts) {
                    if (part is ExprNode.Interp.TextPart) {
                        appendStringEscaped(sb, part.text)
                    } else if (part is ExprNode.Interp.ExprPart) {
                        sb.append("\${")
                        appendExpr(sb, part.expr, Precedence.LOWEST)
                        sb.append('}')
                    }
                }
                sb.append('"')
            }
            is ExprNode.IfExpr -> {
                // if 表达式：body 是 stmt 列表，简化为 if (cond) {...} else {...}
                sb.append("if (")
                appendExpr(sb, node.cond, Precedence.LOWEST)
                sb.append(") {...}")
                if (node.elseBody.isNotEmpty()) {
                    sb.append(" else {...}")
                }
            }
            is ExprNode.When -> {
                // when 表达式：subject + branches，简化展示
                sb.append("when ")
                sb.append('(')
                appendExpr(sb, node.subject, Precedence.LOWEST)
                sb.append(") ")
                sb.append("{...}")
            }
            is ExprNode.Lambda -> {
                sb.append("{ ")
                val params = node.params
                if (params.isNotEmpty()) {
                    for (i in params.indices) {
                        if (i > 0) sb.append(", ")
                        sb.append(params[i])
                    }
                    sb.append(" -> ")
                }
                sb.append("...") // body 是 stmt 列表，省略
                sb.append(" }")
            }
            is ExprNode.FunRef -> {
                sb.append("::").append(node.name)
                if (node.boundArgs.isNotEmpty() || node.boundNamedArgs.isNotEmpty()) {
                    sb.append('(')
                    var first = true
                    for (arg in node.boundArgs) {
                        if (!first) sb.append(", ")
                        appendExpr(sb, arg, Precedence.LOWEST)
                        first = false
                    }
                    for (na in node.boundNamedArgs) {
                        if (!first) sb.append(", ")
                        sb.append(na.name).append(" = ")
                        appendExpr(sb, na.value, Precedence.LOWEST)
                        first = false
                    }
                    sb.append(')')
                }
            }
            is ExprNode.CallExpr -> withPrec(sb, parentPrec, Precedence.POSTFIX) {
                appendExpr(sb, node.callee, Precedence.POSTFIX)
                sb.append('(')
                appendCallArgs(sb, node.args, node.namedArgs)
                sb.append(')')
            }
            is ExprNode.BuiltinCall -> {
                sb.append(node.name).append('(')
                // BuiltinCall 的 NamedArg 与 CallExpr 类型不同（独立内嵌类型），单独处理
                val args = node.args
                for (i in args.indices) {
                    if (i > 0) sb.append(", ")
                    appendExpr(sb, args[i], Precedence.LOWEST)
                }
                val named = node.namedArgs
                for (i in named.indices) {
                    if (args.isNotEmpty() || i > 0) sb.append(", ")
                    sb.append(named[i].name).append(" = ")
                    appendExpr(sb, named[i].value, Precedence.LOWEST)
                }
                sb.append(')')
            }
            else ->
                // 兜底：任何未识别节点用 toString 调试形式，加 "?:" 标记便于发现
                sb.append("?:").append(node)
        }
    }

    private fun appendBinary(sb: StringBuilder, bin: ExprNode.BinaryOp, parentPrec: Int) {
        val prec = binaryPrecedence(bin.op)
        withPrec(sb, parentPrec, prec) {
            appendExpr(sb, bin.left, prec)
            sb.append(' ').append(bin.op).append(' ')
            // 右侧用 prec+1 让左结合操作（如 a - b - c）正确加括号
            appendExpr(sb, bin.right, prec + 1)
        }
    }

    private fun binaryPrecedence(op: String): Int = when (op) {
        "||" -> Precedence.LOGICAL_OR
        "&&" -> Precedence.LOGICAL_AND
        "==", "!=" -> Precedence.EQUALITY
        "<", "<=", ">", ">=" -> Precedence.RELATIONAL
        "+", "-" -> Precedence.ADDITIVE
        "*", "/", "%" -> Precedence.MULTIPLICATIVE
        else -> Precedence.LOWEST
    }

    /** 在指定优先级下渲染：父优先级 > 当前优先级时加括号。 */
    private inline fun withPrec(sb: StringBuilder, parentPrec: Int, currentPrec: Int, body: () -> Unit) {
        val needParen = parentPrec > currentPrec
        if (needParen) sb.append('(')
        body()
        if (needParen) sb.append(')')
    }

    private fun appendArgs(sb: StringBuilder, args: List<ExprNode>) {
        sb.append('(')
        for (i in args.indices) {
            if (i > 0) sb.append(", ")
            appendExpr(sb, args[i], Precedence.LOWEST)
        }
        sb.append(')')
    }

    private fun appendCallArgs(sb: StringBuilder, positional: List<ExprNode>, named: List<ExprNode.CallExpr.NamedArg>) {
        var first = true
        for (arg in positional) {
            if (!first) sb.append(", ")
            appendExpr(sb, arg, Precedence.LOWEST)
            first = false
        }
        for (na in named) {
            if (!first) sb.append(", ")
            sb.append(na.name).append(" = ")
            appendExpr(sb, na.value, Precedence.LOWEST)
            first = false
        }
    }

    private fun appendLiteral(sb: StringBuilder, value: Any?) {
        when (value) {
            null -> sb.append("null")
            is String -> {
                sb.append('"')
                appendStringEscaped(sb, value)
                sb.append('"')
            }
            is Double -> {
                // 保留 toString 输出，但去掉 Java Double 的 ".0" 后缀让小整数显得自然
                val d = value
                if (d == floor(d) && !d.isInfinite()) {
                    sb.append(d.toLong())
                } else {
                    sb.append(d)
                }
            }
            is Boolean -> sb.append(if (value) "true" else "false")
            else -> sb.append(value)
        }
    }

    /** 把字符串内容追加到 sb，转义 " 与 \ 与控制字符。 */
    private fun appendStringEscaped(sb: StringBuilder, s: String) {
        for (i in s.indices) {
            when (val c = s[i]) {
                '"' -> sb.append("\\\"")
                '\\' -> sb.append("\\\\")
                '\n' -> sb.append("\\n")
                '\r' -> sb.append("\\r")
                '\t' -> sb.append("\\t")
                else -> sb.append(c)
            }
        }
    }
}
